import React, { useEffect, useState } from 'react';
import { MapContainer, WMSTileLayer, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-side-by-side';
import 'leaflet-draw/dist/leaflet.draw.css';
import { getCenterAndZoom } from './custom/MapFunctions';
import ExportControl from './custom/MapExportLayers';
import BaseMapLayerControl from './custom/MapChangeMapLayer';
import GeoJsonControl from './custom/MapFranceCaseStudyLayers';
import SpainControl from './custom/MapSpainLayers';
import LeafletControlGeocoder from './custom/MapSearch';
import FranceHabitatsControl from './custom/MapFranceHabitatsContorl';
import { Habitat } from '../../interfaces';
import { HistoricalMapControl } from './custom/MapFranceHisotricalLayers';
import Cookies from 'js-cookie';
import { RailwayMapControl } from './custom/MapFranceRailwayLayers';

interface HabitatsMapProps {
  selectedHabitat: Habitat | null;
}

const HabitatsMap: React.FC<HabitatsMapProps> = ({ selectedHabitat }) => {
  const caseStudy = Cookies.get('caseStudy');
  const [exportControlRef, setExportControlRef] = useState<any>(null);
  const [franceCaseStudyLayerRef, setFranceCaseStudyLayerRef] = useState<any>(null);
  const [spainControlRef, setSpainControlRef] = useState<any>(null);
  const [franceHabitatControlRef, setFranceHabitatControlRef] = useState<any>(null);
  const [franceHistoricalControlRef, setFranceHistoricalControlRef] = useState<any>(null);
  const [franceRailwayLayerRef, setFranceRailwayLayerRef] = useState<any>(null);

  const [habitatLayerConfig, setHabitatLayerConfig] = useState<{
    key: string;
    url: string;
    layers: string;
    zIndex: number;
  } | null>(null);

  // Update map configuration when a new habitat is selected
  useEffect(() => {
    if (caseStudy && selectedHabitat) {
      setHabitatLayerConfig({
        key: `${caseStudy}_habitats_${selectedHabitat.code}`,
        url: `https://maps.plantnet.org/guarden/${caseStudy}/habitats?`,
        layers: `habitat_${selectedHabitat.code}`,
        zIndex: 1000,
      });
    }
  }, [caseStudy, selectedHabitat]);

  // Map Initialization Component
  const MapInitializer: React.FC = () => {
    const map = useMap();

    useEffect(() => {
      if (caseStudy) {
        const { center, zoom } = getCenterAndZoom(caseStudy);
        map.setView(center, zoom);
      }

      if (!exportControlRef) {
        const exportControl = new ExportControl({ position: 'topright' });
        exportControl.addTo(map);
        setExportControlRef(exportControl);
      }

      if (!spainControlRef && caseStudy === 'barcelona') {
        const spainControl = new SpainControl({ position: 'bottomleft' });
        spainControl.addTo(map);
        setSpainControlRef(spainControl);
      }
      if (!franceHabitatControlRef && caseStudy === 'pnr_narbonnaise') {
        const franceHabitatControl = new FranceHabitatsControl({ position: 'bottomright' });
        franceHabitatControl.addTo(map);
        setFranceHabitatControlRef(franceHabitatControl);
      }
      if (!franceHistoricalControlRef && caseStudy === 'pnr_narbonnaise') {
        const franceHistoricalControl = new HistoricalMapControl({ position: 'bottomleft' });
        franceHistoricalControl.addTo(map);
        setFranceHistoricalControlRef(franceHistoricalControl);
      }
      if (!franceRailwayLayerRef && caseStudy === 'pnr_narbonnaise') {
        const exportRailwayLayer = new RailwayMapControl({ position: 'bottomleft' });
        exportRailwayLayer.addTo(map);
        setFranceRailwayLayerRef(exportRailwayLayer);
      }
      if (!franceCaseStudyLayerRef) {
        const franceCaseStudyLayer = new GeoJsonControl({ position: 'bottomleft' });
        franceCaseStudyLayer.addTo(map);
        setFranceCaseStudyLayerRef(franceCaseStudyLayer);
      }
    }, [map]);

    return null;
  };
  return (
    <div>
      <MapContainer
        id='habitatMapId'
        center={[0, 0]}
        zoom={0}
        style={{ height: '800px', width: '100%', borderRadius: '10px' }}
      >
        <LeafletControlGeocoder />
        <MapInitializer />
        <BaseMapLayerControl />
        {/* Case Study Layer */}
        {caseStudy && (
          <WMSTileLayer
            key={`${caseStudy}`}
            url={`https://maps.plantnet.org/guarden/${caseStudy}/indicators`}
            layers={'extent'}
            format='image/png'
            transparent={true}
            version='1.3.0'
            opacity={0.5}
            attribution='Your attribution text'
            zIndex={999}
          />
        )}
        {/* Habitat Layer (Dynamically Updates) */}
        {habitatLayerConfig && (
          <WMSTileLayer
            key={habitatLayerConfig.key}
            url={habitatLayerConfig.url}
            layers={habitatLayerConfig.layers}
            format='image/png'
            transparent={true}
            version='1.1.1'
            attribution='Your attribution text'
            zIndex={habitatLayerConfig.zIndex}
          />
        )}
      </MapContainer>
    </div>
  );
};

export { HabitatsMap };
